import { last } from "lodash";
import { isLastMessageSame } from "@/react/components/Chat/ChatWindowV2/helpers";
import type { ChatRoomMessage } from "@/react/types/Chat/ChatRoomMessage";
import { MAX_MESSAGES_THRESHOLD, MESSAGES_THRESHOLD } from "../constants";
import type {
  ChatRoomActionsSliceState,
  ChatRoomMessageActionsSliceState,
  ChatRoomsSliceState,
} from "../types";

// Batching configuration
export const BATCH_DELAY_MS = 200;

// Batch queue for each chat room
export const batchQueues = new Map<
  string,
  {
    messages: Array<{
      message: ChatRoomMessage;
    }>;
    timeoutId: NodeJS.Timeout | null;
  }
>();

const processMessagesBatch = (
  currentMessages: ChatRoomMessage[],
  newMessages: Array<{ message: ChatRoomMessage }>,
): ChatRoomMessage[] => {
  let messagesArray = [...currentMessages];
  const hasMessageThresholdReached =
    messagesArray.length > MAX_MESSAGES_THRESHOLD;

  if (hasMessageThresholdReached) {
    messagesArray = messagesArray.slice(-MESSAGES_THRESHOLD);
  }

  // Process each message in the batch
  for (const { message } of newMessages) {
    const lastMessageInArr = last(messagesArray);
    const isNewMessageReached = lastMessageInArr?.id != message.id;

    if (isNewMessageReached && !isLastMessageSame(lastMessageInArr, message)) {
      const indexOfMessageWithSameUUID = messagesArray.findIndex(
        (m: ChatRoomMessage) => m.creation_uuid === message.creation_uuid,
      );
      if (indexOfMessageWithSameUUID > -1) {
        messagesArray[indexOfMessageWithSameUUID] = message;
      } else {
        messagesArray.push(message);
      }
    }
  }

  return messagesArray;
};

export const processBatchedMessages = (
  uuid: string,
  set: (fn: (state: ChatRoomsSliceState) => void) => void,
  get: () => ChatRoomsSliceState &
    ChatRoomActionsSliceState &
    ChatRoomMessageActionsSliceState,
) => {
  if (!batchQueues) {
    return;
  }
  const queue = batchQueues.get(uuid);
  if (!queue || queue.messages.length === 0) {
    return;
  }

  const messages = [...queue.messages];
  queue.messages = [];
  queue.timeoutId = null;

  try {
    // Get current state to check conditions
    const currentState = get();
    const chatRoomState = currentState.chatRoomStateMap.get(uuid);

    if (!chatRoomState) {
      return;
    }

    const uiState = chatRoomState.uiState;
    const isInitialPageLoaded = !!uiState?.isInitialPageLoaded;
    const hasNextPage = !!uiState?.hasNextPage;

    if (!isInitialPageLoaded || hasNextPage) {
      return;
    }

    // Process all messages in the batch
    set(state => {
      const chatRoomState = state.chatRoomStateMap.get(uuid);
      if (!chatRoomState) {
        return;
      }

      const processedMessages = processMessagesBatch(
        chatRoomState?.chatRoomMessages ?? [],
        messages,
      );

      // Update UI state if threshold was reached
      if (processedMessages.length > MAX_MESSAGES_THRESHOLD) {
        chatRoomState.uiState.firstMessageId = processedMessages[0].id;
        chatRoomState.uiState.hasPrevPage = true;
      }

      chatRoomState.chatRoomMessages = processedMessages;
    });
  } catch (error) {
    console.error("Error processing batched messages:", error);
  }
};
